import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import './../Login.css';
import config from '../config';
import blue_rock_icon from '../assets/blue_rock_icon.png';
import PaperTrade_logo from '../assets/PaperTrade_logo.png';
import learnFNO_small_t from '../assets/learnFNO_small_t.png';
import money_mingle from '../assets/moneyMingle.png';
import triqutra_small from '../assets/Triqutra_small.png';
import money_zone_icon from '../assets/money_zone_icon.png';
import { storeToken, consumeAuthMessage } from '../utils/auth';
import { apiAppFetch } from '../utils/apiFetch';


// Icon component for inline SVG icons
const Icon = ({ name, className = "" }) => {
  const paths = {
    mail: "M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z",
    lock: "M17 8h-1V6a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2zm-7-2a2 2 0 1 1 4 0v2h-4V6z",
    eye: "M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7zm0 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8z",
    eyeOff: "M2 4l2.2 2.2C2.9 7.5 2 9 2 9s3 7 10 7c2 0 3.7-.6 5.1-1.5L20 17l2-2-18-11zm10 10c-3.1 0-5.4-2.7-6.6-4.4.5-.8 1.3-1.9 2.5-2.9l1.6 1.0A4 4 0 0 0 12 14zm0-8c.4 0 .8.1 1.2.2l2.2 1.4c.3.4.5.9.6 1.4.0.1 0 .3 0 .4 0 2.2-1.8 4-4 4-.4 0-.7-.1-1.1-.2l-2.1-1.3c-.5-.7-.8-1.5-.8-2.5 0-2.2 1.8-4 4-4z",
    person: "M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
  };
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={`icon ${className}`} fill="currentColor">
      <path d={paths[name]} />
    </svg>
  );
};

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [hostnamee, setHostname] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(() => {
    const stored = consumeAuthMessage();
    if (stored) {
      return stored;
    }
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('session') === 'expired') {
        return 'Session expired. Please login again.';
      }
    } catch (e) {}
    return '';
  });
  const [touched, setTouched] = useState({ username: false, password: false });
  const { isDarkMode } = useTheme();

  const token = localStorage.getItem('authToken');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault(); 
    setTouched({ username: true, password: true });
    setIsLoading(true);
    setError('');

    if (!username.trim() || !password) {
      setIsLoading(false);
      return;
    }

    const data = {
      username: username,
      password: password,
    };

    try {
      const response = await apiAppFetch('login', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      const resData = await response.json();
      if (resData.success) {
        storeToken(resData);

        if(resData.initSetupDone==0){
          navigate('/change-password');
          return;
        }

        navigate('/?appilix_push_notification_user_identity='+resData.clientId);
      } else {
        setError(resData.message || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    setHostname(window.location.hostname);
  }, []);

  const getLogoImage = () => {
    if (hostnamee.includes('blue')) {
      return blue_rock_icon;
    } else if (hostnamee.includes('papertrade')) {
      return PaperTrade_logo;
    } else if (hostnamee.includes('learnfno')) {
      return learnFNO_small_t;
    } else if (hostnamee.includes('moneymingle')) {
      return money_mingle;
    } else if (hostnamee.includes('triqutra')) {
      return triqutra_small;
    } else if (hostnamee.includes('moneyzone')) {
      return money_zone_icon;
    }
    return PaperTrade_logo;
  };

  const usernameError = touched.username && !username.trim() ? 'User ID is required.' : '';
  const passwordError = touched.password && !password ? 'Password is required.' : '';
  const canSubmit = !usernameError && !passwordError && username.trim() && password && !isLoading;

  return token ? (
    <Navigate to="/" />
  ) : (
    <div className={`login-container ${isDarkMode ? 'dark-theme' : 'light-theme'}`} data-theme={isDarkMode ? 'dark' : 'light'}>
      <div className="login-wrapper">
        <div className="login-content">
          {/* Logo Section */}
          <div className="logo-section">
            <img 
              src={getLogoImage()} 
              alt="Company Logo" 
              className="company-logo"
            />
            <h1 className="login-title">Welcome back</h1>
            <p className="login-subtitle">Sign in to your account to continue.</p>
          </div>

          {/* Login Form */}
          <div className="login-card">
            <div className="login-card-content">
              <form onSubmit={handleSubmit} className="login-form" noValidate>
                {/* Error Alert */}
                {error && (
                  <div className="error-alert" role="alert">
                    {error}
                  </div>
                )}

                {/* Username Field */}
                <div className="form-field">
                  <label className="field-label">User ID</label>
                  <div className="input-wrapper">
                    <span className="input-icon">
                      <Icon name="person" />
                    </span>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      onBlur={() => setTouched(t => ({ ...t, username: true }))}
                      placeholder="Enter your user ID"
                      autoComplete="username"
                      className={`login-input ${usernameError ? 'input-error' : ''}`}
                      disabled={isLoading}
                    />
                  </div>
                  {usernameError && <p className="field-error">{usernameError}</p>}
                </div>

                {/* Password Field */}
                <div className="form-field">
                  <label className="field-label">Password</label>
                  <div className="input-wrapper">
                    <span className="input-icon">
                      <Icon name="lock" />
                    </span>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onBlur={() => setTouched(t => ({ ...t, password: true }))}
                      placeholder="••••••••"
                      autoComplete="current-password"
                      className={`login-input password-input ${passwordError ? 'input-error' : ''}`}
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={handleTogglePasswordVisibility}
                      className="password-toggle"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      disabled={isLoading}
                    >
                      <Icon name={showPassword ? 'eyeOff' : 'eye'} />
                    </button>
                  </div>
                  {passwordError && <p className="field-error">{passwordError}</p>}
                </div>

                {/* Login Button */}
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className={`login-button ${canSubmit ? 'button-enabled' : 'button-disabled'}`}
                >
                  {isLoading ? (
                    <span className="button-loading">
                      <span className="spinner"></span>
                      Signing in…
                    </span>
                  ) : (
                    'Sign in'
                  )}
                </button>
              </form>
            </div>

            {/* Register Link */}
            {hostnamee.includes('learnfno') && (
              <div className="register-section">
                <p className="register-text">
                  Don't have an account?{' '}
                  <Link to="/register" className="register-link">
                    Register here
                  </Link>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
